<template>
  <div
    class="z-50 max-h-56 min-w-64 max-w-80 overflow-auto rounded-md border border-obsidian-200 bg-white p-1 shadow-lg"
  >
    <template v-for="(item, index) in items" :key="`${item.entityType}-${item.id}`">
      <p
        v-if="index === 0 || items[index - 1]?.group !== item.group"
        class="px-2 pb-0.5 pt-1.5 text-sm font-semibold uppercase tracking-wider text-obsidian-400"
      >
        {{ t(item.group) }}
      </p>
      <button
        type="button"
        class="flex w-full flex-col rounded-sm px-2 py-1 text-left transition-colors"
        :class="
          index === selectedIndex
            ? 'bg-hazel-100 text-obsidian-900'
            : 'text-obsidian-700 hover:bg-hazel-50'
        "
        @click="selectItem(index)"
        @mouseenter="selectedIndex = index"
      >
        <LicencePlate v-if="item.entityType === 'car'" class="mb-0 self-start">
          {{ item.label }}
        </LicencePlate>
        <span v-else class="truncate text-sm font-medium">{{ item.label }}</span>
        <span v-if="item.secondary" class="truncate text-xs text-obsidian-400">
          {{ item.secondary }}
        </span>
      </button>
    </template>
    <p v-if="!items.length" class="px-2 py-1.5 text-sm text-obsidian-500">
      {{ query.trim() ? t('noResults') : t('emptyState') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LicencePlate from '@/components/LicencePlate.vue';
import type { EntityLinkItem } from './comment-entity-link';

const props = defineProps<{
  command: (item: Pick<EntityLinkItem, 'entityType' | 'id' | 'label'>) => void;
  items: EntityLinkItem[];
  query: string;
}>();

const { t } = useI18n();
const selectedIndex = ref(0);

watch(
  () => props.items,
  () => {
    selectedIndex.value = 0;
  },
);

const selectItem = (index: number) => {
  const item = props.items[index];
  if (!item) return;
  props.command({ id: item.id, label: item.label, entityType: item.entityType });
};

const onKeyDown = ({ event }: { event: KeyboardEvent }) => {
  if (event.key === 'ArrowUp') {
    selectedIndex.value =
      (selectedIndex.value + props.items.length - 1) % Math.max(props.items.length, 1);
    return true;
  }
  if (event.key === 'ArrowDown') {
    selectedIndex.value = (selectedIndex.value + 1) % Math.max(props.items.length, 1);
    return true;
  }
  if (event.key === 'Enter') {
    selectItem(selectedIndex.value);
    return true;
  }
  return false;
};

defineExpose({ onKeyDown });
</script>

<i18n lang="json">
{
  "en": {
    "bookings": "Bookings",
    "invoices": "Invoices",
    "customers": "Customers",
    "cars": "Vehicles",
    "emptyState": "Type to search",
    "noResults": "No results found"
  },
  "de": {
    "bookings": "Buchungen",
    "invoices": "Rechnungen",
    "customers": "Kunden",
    "cars": "Fahrzeuge",
    "emptyState": "Tippen zum Suchen",
    "noResults": "Keine Ergebnisse gefunden"
  },
  "es": {
    "bookings": "Reservas",
    "invoices": "Facturas",
    "customers": "Clientes",
    "cars": "Vehículos",
    "emptyState": "Escribe para buscar",
    "noResults": "No se encontraron resultados"
  }
}
</i18n>
